import { z } from "zod";

/** Label credentials and PIN-only login use exactly four digits. */
export const labelPinSetupValueSchema = z.string().regex(/^\d{4}$/u);
export const labelPinLoginValueSchema = labelPinSetupValueSchema;

export const roleSchema = z.enum(["coordinator", "sales", "marketing", "quality", "printer"]);
export type Role = z.infer<typeof roleSchema>;
export const roleLabels: Record<Role, string> = {
  coordinator: "Koordinasyon", sales: "Satış", marketing: "Pazarlama", quality: "Kalite", printer: "Etiketçi",
};
export const actorSchema = z.object({
  id: z.string().min(1), tenantId: z.string().min(1), displayName: z.string().min(1),
  roles: z.array(roleSchema),
  capabilities: z.array(z.enum(["label_work_order_request_create", "label_plan_source_read"])).optional(),
  customerCodes: z.array(z.string()).nullable(),
  requestIds: z.array(z.string()).nullable(),
}).strict();
export type Actor = z.infer<typeof actorSchema>;

export const stateSchema = z.enum([
  "assignment_pending", "sales_pending", "marketing", "quality_review",
  "returned_to_sales", "print_info_missing", "print_ready", "print_completed",
]);
export type RequestState = z.infer<typeof stateSchema>;
export const stateLabels: Record<RequestState, string> = {
  assignment_pending: "Atama bekliyor", sales_pending: "Satış bilgisi bekliyor", marketing: "Pazarlamada",
  quality_review: "Kalite incelemesinde", returned_to_sales: "Satışa geri döndü",
  print_info_missing: "Baskı bilgisi eksik", print_ready: "Baskıya hazır", print_completed: "Baskı tamamlandı",
};

export const sourceCellSchema = z.object({
  address: z.string(), text: z.string(), raw: z.unknown(), formula: z.string().nullable(), error: z.string().nullable(),
});
export const planRowSchema = z.object({
  id: z.string(), sourceId: z.string(), workbookName: z.string(), sheetName: z.string(), rowNumber: z.number().int(),
  workOrder: z.string().nullable(), orderNumber: z.string().nullable(), lot: z.string().nullable(),
  customerCode: z.string().nullable(), customerName: z.string().nullable(), sku: z.string().nullable(), productName: z.string().nullable(),
  lineName: z.string().nullable().optional(),
  productionAmountRaw: z.string().nullable(), productionUnitRaw: z.string().nullable(),
  sourceNotes: z.array(z.object({ address: z.string(), text: z.string() })),
  cells: z.array(sourceCellSchema),
  classification: z.enum(["required", "not_required", "verify"]), classificationReason: z.string(),
});
export type PlanRow = z.infer<typeof planRowSchema>;
export const planPreviewSchema = z.object({
  sourceId: z.string(), workbookName: z.string(), sha256: z.string(), importedAt: z.string(),
  rows: z.array(planRowSchema), warnings: z.array(z.string()), repeated: z.boolean(),
});
export type PlanPreview = z.infer<typeof planPreviewSchema>;

export const quantityBasisSchema = z.object({
  planUnit: z.enum(["koli", "kg"]).nullable(),
  labelTarget: z.enum(["koli", "poşet", "birim"]).nullable(),
  targetsPerCase: z.number().int().positive().max(100000).nullable(),
  kgPerCase: z.number().positive().max(100000).nullable(),
  labelsPerTarget: z.number().int().positive().max(100).nullable(),
}).strict();
export type QuantityBasis = z.infer<typeof quantityBasisSchema>;
export const emptyQuantityBasis: QuantityBasis = {
  planUnit: null, labelTarget: null, targetsPerCase: null, kgPerCase: null, labelsPerTarget: 1,
};

export const requirementsSchema = z.object({
  workflowMode: z.enum(["use_existing", "change_existing", "new"]).nullable().default(null),
  sourceFileId: z.string().uuid().nullable().default(null),
  preserveDimensions: z.boolean().default(true),
  customerRequest: z.string().max(10000), packaging: z.string().max(1000), labelKind: z.string().max(200),
  widthMm: z.number().positive().max(10000).nullable(), heightMm: z.number().positive().max(10000).nullable(),
  texts: z.string().max(30000), language: z.string().max(160), referenceNotes: z.string().max(10000),
  referenceFileIds: z.array(z.string().uuid()).max(20), requestedPrintQuantity: z.number().int().positive().max(100000000).nullable(),
  quantityBasis: quantityBasisSchema.nullable().default(null),
}).strict();
export type Requirements = z.infer<typeof requirementsSchema>;
export const emptyRequirements: Requirements = {
  workflowMode: null, sourceFileId: null, preserveDimensions: true,
  customerRequest: "", packaging: "", labelKind: "", widthMm: null, heightMm: null, texts: "", language: "",
  referenceNotes: "", referenceFileIds: [], requestedPrintQuantity: null, quantityBasis: null,
};
export const requirementsRevisionSchema = z.object({
  id: z.string(), number: z.number().int(), requirements: requirementsSchema,
  createdBy: z.string(), createdByName: z.string(), createdAt: z.string(),
});
export type RequirementsRevision = z.infer<typeof requirementsRevisionSchema>;
export const fileVersionSchema = z.object({
  id: z.string(), number: z.number().int(), kind: z.enum(["artwork", "reference"]),
  originalName: z.string(), contentType: z.string(), size: z.number().int(), sha256: z.string(),
  changeNote: z.string(), requirementsRevisionId: z.string().nullable(),
  uploadedBy: z.string(), uploadedByName: z.string(), uploadedAt: z.string(),
  scanStatus: z.enum(["clean", "unverified"]).default("unverified"),
});
export type FileVersion = z.infer<typeof fileVersionSchema>;
export type StoredFile = { storageKey: string; originalName: string; contentType: string; size: number;
  sha256: string; scanStatus: "clean" | "unverified" };

export const correctionSchema = z.object({ field: z.string().trim().min(1).max(100), message: z.string().trim().min(1).max(2000) }).strict();
export const reviewSchema = z.object({
  id: z.string(), cycle: z.number().int(), requirementsRevisionId: z.string(), fileVersionId: z.string(),
  submittedBy: z.string(), submittedAt: z.string(), decision: z.enum(["pending", "approved", "rejected"]),
  decidedBy: z.string().nullable(), decidedByName: z.string().nullable(), decidedAt: z.string().nullable(),
  reason: z.string().nullable(), corrections: z.array(correctionSchema),
});
export type Review = z.infer<typeof reviewSchema>;
export const printDeclarationSchema = z.object({
  id: z.string(), reviewId: z.string(), fileVersionId: z.string(), quantity: z.number().int().positive(),
  operatorId: z.string(), operatorName: z.string(), recordedAt: z.string(), evidence: z.literal("operator_declaration"),
});
export const historyEventSchema = z.object({
  id: z.string(), type: z.string(), actorId: z.string(), actorName: z.string(), occurredAt: z.string(),
  summary: z.string(), details: z.record(z.string(), z.unknown()),
});
export const notificationSchema = z.object({
  id: z.string(), event: z.string(), targetRole: roleSchema, targetUserId: z.string().nullable(),
  status: z.enum(["awaiting_mapping", "queued", "processing", "failed", "simulated", "accepted", "delivery_unknown", "superseded"]),
  attempts: z.number().int(), lastError: z.string().nullable(), updatedAt: z.string(),
});
export type NotificationJob = z.infer<typeof notificationSchema>;
export const requestSummarySchema = z.object({
  lineName: z.string().nullable().default(null), workOrder: z.string().nullable().default(null), planExplanation: z.string().default(""),
  id: z.string(), requestNo: z.string(), variantKey: z.string(), labelKind: z.string(),
  planRowId: z.string().nullable(), planReference: z.string(), customerCode: z.string().nullable(), customerName: z.string().nullable(),
  productName: z.string().nullable(), sku: z.string().nullable(), packaging: z.string(),
  state: stateSchema, version: z.number().int().positive(), responsibleRole: roleSchema,
  salesUserId: z.string().nullable(), salesUserName: z.string().nullable(),
  expectedAction: z.string(), qualityWarning: z.string().nullable(), updatedAt: z.string(), dueAt: z.string().nullable(),
  requestedPrintQuantity: z.number().int().positive().nullable(), approvedFileVersionId: z.string().nullable(),
});
export type RequestSummary = z.infer<typeof requestSummarySchema>;
export const requestDetailSchema = requestSummarySchema.extend({
  planRow: planRowSchema.nullable(), draft: requirementsSchema, requirementsRevisions: z.array(requirementsRevisionSchema),
  files: z.array(fileVersionSchema), reviews: z.array(reviewSchema),
  printDeclarations: z.array(printDeclarationSchema), history: z.array(historyEventSchema), notifications: z.array(notificationSchema),
});
export type RequestDetail = z.infer<typeof requestDetailSchema>;
export const listQuerySchema = z.object({
  q: z.string().max(200).default(""), state: stateSchema.optional(), department: roleSchema.optional(),
  responsible: z.string().max(200).optional(), customer: z.string().max(200).optional(), plan: z.string().max(200).optional(),
  overdue: z.enum(["true", "false"]).default("false"),
  sort: z.enum(["updatedAt", "requestNo", "customer", "state"]).default("updatedAt"),
  direction: z.enum(["asc", "desc"]).default("desc"), page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
}).strict();
export type ListQuery = z.infer<typeof listQuerySchema>;
export const requestListSchema = z.object({ items: z.array(requestSummarySchema), total: z.number().int(), page: z.number().int(), pageSize: z.number().int() });
export type RequestList = z.infer<typeof requestListSchema>;

export type CommandMeta = { idempotencyKey: string; expectedVersion: number };
export const commandVersionSchema = z.object({ expectedVersion: z.number().int().positive() });
export const createRequestSchema = z.object({
  planRowId: z.string().uuid(), variantKey: z.string().trim().min(1).max(160), labelKind: z.string().trim().min(1).max(200),
  confirmed: z.literal(true), salesUserId: z.string().min(1).nullable().default(null), dueAt: z.string().datetime().nullable().default(null),
}).strict();
export type CreateRequestInput = z.infer<typeof createRequestSchema>;
export const draftCommandSchema = commandVersionSchema.extend({ requirements: requirementsSchema }).strict();
export const submitCommandSchema = commandVersionSchema.strict();
export const assignCommandSchema = commandVersionSchema.extend({ salesUserId: z.string().min(1) }).strict();
export const reviewCommandSchema = commandVersionSchema.extend({ fileVersionId: z.string().uuid() }).strict();
export const decisionCommandSchema = commandVersionSchema.extend({
  reviewId: z.string().uuid(), decision: z.enum(["approve", "reject"]), reason: z.string().trim().max(5000),
  corrections: z.array(correctionSchema).max(50),
}).strict();
export type DecisionInput = Omit<z.infer<typeof decisionCommandSchema>, "expectedVersion">;
export const printCommandSchema = commandVersionSchema.extend({
  reviewId: z.string().uuid(), fileVersionId: z.string().uuid(), quantity: z.number().int().positive().max(100000000),
  declaration: z.literal(true),
}).strict();
export type PrintInput = Omit<z.infer<typeof printCommandSchema>, "expectedVersion">;
export const printIssueCommandSchema = commandVersionSchema.extend({ reason: z.string().trim().min(3).max(3000) }).strict();

export const policySchema = z.object({
  status: z.enum(["proposal", "confirmed"]), requiredSalesFields: z.array(z.enum([
    "customerRequest", "packaging", "labelKind", "widthMm", "heightMm", "texts", "language", "requestedPrintQuantity",
  ])), fileTypes: z.array(z.string()), maxFileBytes: z.number().int().positive(),
});
export type LabelPolicy = z.infer<typeof policySchema>;
export const proposedPolicy: LabelPolicy = {
  status: "proposal", requiredSalesFields: ["customerRequest", "packaging", "labelKind", "texts", "language"],
  fileTypes: ["application/pdf", "image/png", "image/jpeg"], maxFileBytes: 10 * 1024 * 1024,
};
/** Factory workflow approved 23 October; activation remains independently gated. */
export const confirmedInternalLabelPolicy: LabelPolicy = {
  ...proposedPolicy, status: "confirmed",
  requiredSalesFields: ["customerRequest", "packaging", "requestedPrintQuantity"],
};
export const directoryEntrySchema = z.object({ id: z.string().min(1), displayName: z.string().min(1), roles: z.array(roleSchema) });
export type DirectoryEntry = z.infer<typeof directoryEntrySchema>;
export const sessionSchema = z.object({
  actor: actorSchema.nullable(), demo: z.boolean(), csrfToken: z.string().nullable(),
  policy: policySchema, pinMode: z.enum(["undecided", "email_otp", "persistent"]),
  pinLoginAvailable: z.boolean().default(false),
  pinSetupAvailable: z.boolean().default(false),
  authenticationMethod: z.enum(["corporate", "pin"]).nullable().optional(),
  directory: z.array(directoryEntrySchema),
});
type ParsedSessionInfo = z.infer<typeof sessionSchema>;
export type SessionInfo = Omit<ParsedSessionInfo, "pinSetupAvailable"> & { pinSetupAvailable?: boolean };
export const apiErrorSchema = z.object({
  error: z.object({ code: z.string(), message: z.string(), fields: z.record(z.string(), z.string()).optional() }),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

export class DomainError extends Error {
  constructor(public readonly code: string, message: string, public readonly status = 409, public readonly fields?: Record<string, string>) {
    super(message);
    this.name = "DomainError";
  }
}
